import { skyCathedralPose, skyCathedralKneelSweep } from './100-sky-cathedral-pose.mjs';
import { skyCathedralSweep, SKY_CATHEDRAL_SPACE } from './99-sky-cathedral-space.mjs';
import { skyProjectileContact } from './80-sky-aim.mjs';
import { skyFlightFrameFromTangent } from './92-sky-flight-frame.mjs';

/** Blockout tuning, in active seconds and HP. No campaign activation or generic
 * boss deadline: missed grounding/entry opportunities repeat indefinitely. */
export const SKY_CATHEDRAL_POLICY = Object.freeze({
  arrivalSeconds: 2,
  trackingSeconds: 2,
  commitSeconds: 1.2,
  releaseSeconds: 0.25,
  recoverySeconds: 2,
  lockSeconds: 6.6,
  aimPrepareSeconds: 0.6,
  kneeHp: 12,
  arrestSeconds: 2,
  generatorHp: 24,
  kneelSeconds: 5,
  dischargeSeconds: 4,
  lossSeconds: 3,
  maximumPilotSpeed: 24,
});
const IDS = SKY_CATHEDRAL_SPACE.legIds;
const finite = (p) => p && ['x', 'y', 'z'].every((axis) => Number.isFinite(p[axis]));
const copy = (p) => ({ x: p.x, y: p.y, z: p.z });
const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
const dot = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
const sub = (a, b) => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const clamp = (n) => Math.max(0, Math.min(1, n));
const smooth = (n) => {
  const t = clamp(n);
  return t * t * (3 - 2 * t);
};
const identity = skyFlightFrameFromTangent({ x: 0, y: 0, z: 0 }, { x: 0, y: 0, z: -1 });
const finished = (e) => ['complete', 'lost'].includes(e.phase);
const active = (e) => ['grounding', 'underpass', 'nave'].includes(e.phase);

function event(e, kind, component = null, point = null) {
  const value = { id: ++e.eventId, kind, component, phase: e.phase, time: e.age };
  if (finite(point)) value.point = copy(point);
  e.events.push(value);
  if (e.events.length > 64) e.events.shift();
}
function enter(e, phase) {
  if (phase === 'pilot-down') {
    e.terminalPose = e.pose;
    e.motionHeld = false;
  }
  e.phase = phase;
  e.phaseAge = 0;
  e.strike = null;
  event(e, 'phase-entered');
}
const heldLegs = (e) => IDS.filter((id) => !e.legs[id].broken && e.legs[id].lockedUntil > e.age);
const openLegs = (e) =>
  heldLegs(e).filter((id) => e.age - e.legs[id].lockAt >= SKY_CATHEDRAL_POLICY.aimPrepareSeconds);

/** Pose input is the single bridge to100: locked feet retain their captured
 * stance; ordinary gait time cannot advance underneath a held mechanism. */
export function skyCathedralPoseInput(e) {
  const collapsed = ['kneel', 'nave', 'discharge', 'complete'].includes(e.phase);
  return {
    time: e.age,
    geometry: e.geometry,
    gaitTime: e.gaitTime,
    travel: 0,
    lockedLegs: heldLegs(e),
    brokenLegs: IDS.filter((id) => e.legs[id].broken),
    openLegs: openLegs(e),
    armorOpen: Object.fromEntries(
      IDS.map((id) => {
        const leg = e.legs[id],
          duration = SKY_CATHEDRAL_POLICY.aimPrepareSeconds;
        return [
          id,
          leg.broken
            ? 1
            : leg.lockAt == null
              ? 0
              : e.age < leg.lockedUntil
                ? smooth((e.age - leg.lockAt) / duration)
                : 1 - smooth((e.age - leg.lockedUntil) / duration),
        ];
      }),
    ),
    damageProgress: Object.fromEntries(
      IDS.map((id) => [id, e.legs[id].broken ? smooth((e.age - e.legs[id].brokenAt) / 0.6) : 0]),
    ),
    lockPose: e.lockPose,
    sag:
      e.phase === 'arrest'
        ? smooth(e.phaseAge / SKY_CATHEDRAL_POLICY.arrestSeconds)
        : ['underpass', 'kneel', 'nave', 'discharge', 'complete'].includes(e.phase)
          ? 1
          : 0,
    kneel: collapsed
      ? e.phase === 'kneel'
        ? smooth(e.phaseAge / SKY_CATHEDRAL_POLICY.kneelSeconds)
        : 1
      : 0,
    generatorOpen: ['nave', 'discharge', 'complete'].includes(e.phase),
  };
}
function refresh(e) {
  if (e.terminalPose && ['pilot-down', 'lost'].includes(e.phase)) return (e.pose = e.terminalPose);
  e.pose = skyCathedralPose(skyCathedralPoseInput(e));
  return e.pose;
}

/** Freeze the current physical pose after the caller accepts the last shield
 * loss. This transition advances no clock and is idempotent; presentation owns
 * the campaign's finite loss sequence after its run status becomes terminal. */
export function failSkyCathedralPilot(e) {
  if (!e || finished(e) || e.phase === 'pilot-down') return e;
  enter(e, 'pilot-down');
  refresh(e);
  return e;
}

/** Standalone, unstarted blockout encounter. Pilot/world positions are metres.
 * Malformed positions fail closed; all objectives begin intact. This factory
 * has no dependency on the campaign and grants no completion or equipment. */
export function createSkyCathedral({ pilot = { x: 0, y: 8, z: -30 }, geometry = null } = {}) {
  if (!finite(pilot) || Math.max(...Object.values(pilot).map(Math.abs)) > 200) return null;
  const e = {
    geometry,
    phase: 'arrival',
    phaseAge: 0,
    age: 0,
    gaitTime: 0,
    pilot: copy(pilot),
    previousPilot: copy(pilot),
    legs: Object.fromEntries(
      IDS.map((id) => [
        id,
        { hp: SKY_CATHEDRAL_POLICY.kneeHp, broken: false, lockedUntil: 0, lockAt: null },
      ]),
    ),
    generator: { hp: SKY_CATHEDRAL_POLICY.generatorHp, broken: false },
    lockPose: null,
    strike: null,
    strikeId: 0,
    nextStrikeAt: 0,
    enteredUnderpass: false,
    exitCrossed: false,
    exitedUnderpass: false,
    enteredNave: false,
    eventId: 0,
    events: [],
    hitLedger: {},
    portalPasses: {},
    outcome: null,
  };
  refresh(e);
  return e;
}

function crossed(e, key, portal, from, to, radius) {
  if (!portal || !finite(portal.center) || !finite(portal.normal)) return false;
  const a = sub(from, portal.center),
    b = sub(to, portal.center);
  const start = dot(a, portal.normal),
    end = dot(b, portal.normal);
  if (start <= -radius) e.portalPasses[key] = true;
  if (!e.portalPasses[key]) return false;
  // Keep the approach witness across real fixed steps. Every portion inside
  // the portal slab must fit the whole pilot, not just its centre on one tick.
  const low = Math.min(start, end),
    high = Math.max(start, end);
  if (low <= radius && high >= -radius) {
    for (const signed of [Math.max(-radius, low), Math.min(radius, high)]) {
      const t = Math.abs(end - start) > 1e-10 ? (signed - start) / (end - start) : 0;
      const at = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, z: a.z + (b.z - a.z) * t };
      if (
        Math.abs(dot(at, portal.right)) > portal.halfWidth - radius ||
        Math.abs(dot(at, portal.up)) > portal.halfHeight - radius
      ) {
        e.portalPasses[key] = false;
        return false;
      }
    }
  }
  if (end >= radius && start < radius) {
    e.portalPasses[key] = false;
    return true;
  }
  return false;
}

function catchHit(e, strike) {
  const hits = IDS.filter((id) => !e.legs[id].broken)
    .map((id) => {
      const socket = e.pose.legs[id].catchpoint;
      const hit = skyProjectileContact(
        {
          kind: 'pillar',
          contactShape: 'box',
          ...socket.center,
          halfExtents: socket.halfExtents,
          hp: Infinity,
        },
        strike.origin,
        strike.target,
        identity,
      );
      return hit ? { id, hit, distance: distance(strike.origin, hit) } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.distance - b.distance);
  const caught = hits[0];
  if (!caught) return null;
  const obstruction = skyCathedralSweep(strike.origin, strike.target, e.pose, 0);
  if (
    obstruction &&
    obstruction.fraction * distance(strike.origin, strike.target) + 0.01 < caught.distance
  )
    return null;
  return caught;
}

function lightning(e, dt) {
  if (e.phase !== 'grounding') return;
  if (!e.strike && e.age >= e.nextStrikeAt && !heldLegs(e).length) {
    e.strike = {
      id: ++e.strikeId,
      age: 0,
      stage: 'tracking',
      target: copy(e.pilot),
      origin: { x: e.pilot.x, y: e.pilot.y + 50, z: e.pilot.z },
      legId: null,
    };
    event(e, 'lightning-tracking');
  }
  const strike = e.strike;
  if (!strike) return;
  strike.age += dt;
  const p = SKY_CATHEDRAL_POLICY;
  if (strike.stage === 'tracking') {
    strike.target = copy(e.pilot);
    strike.origin = { x: e.pilot.x, y: e.pilot.y + 50, z: e.pilot.z };
    if (strike.age >= p.trackingSeconds) {
      strike.stage = 'committed';
      event(e, 'lightning-committed', null, strike.target);
    }
  }
  if (strike.stage === 'committed' && strike.age >= p.trackingSeconds + p.commitSeconds) {
    strike.stage = 'active';
    const caught = catchHit(e, strike);
    if (caught) {
      strike.legId = caught.id;
      strike.target = copy(caught.hit);
      e.legs[caught.id].lockedUntil = e.age + p.lockSeconds;
      e.legs[caught.id].lockAt = e.age;
      e.lockPose ??= e.pose;
      event(e, 'leg-locked', caught.id, caught.hit);
    }
    event(e, 'lightning-released', strike.legId, strike.target);
  }
  if (strike.age >= p.trackingSeconds + p.commitSeconds + p.releaseSeconds) {
    e.strike = null;
    e.nextStrikeAt = e.age + p.recoverySeconds;
  }
}

/** Advance only validated physical movement and clocks. A caller cannot jump
 * through portals: movement beyond24m/s, invalid dt or nonfinite data leaves
 * the encounter unchanged. Damage is accepted separately from real contacts. */
export function stepSkyCathedral(
  e,
  dt,
  { pilot, pilotAlive = true, activeProjectileIds = [] } = {},
) {
  if (
    !e ||
    finished(e) ||
    !Number.isFinite(dt) ||
    dt <= 0 ||
    dt > 0.1 ||
    !finite(pilot) ||
    distance(pilot, e.pilot) > SKY_CATHEDRAL_POLICY.maximumPilotSpeed * dt + 1e-6
  )
    return e;
  e.previousPilot = copy(e.pilot);
  e.pilot = copy(pilot);
  e.age += dt;
  e.phaseAge += dt;
  const ids = new Set(activeProjectileIds.filter(Number.isSafeInteger));
  for (const key of Object.keys(e.hitLedger)) if (!ids.has(Number(key))) delete e.hitLedger[key];
  if (!pilotAlive) failSkyCathedralPilot(e);
  if (e.phase === 'arrival' && e.phaseAge >= SKY_CATHEDRAL_POLICY.arrivalSeconds)
    enter(e, 'grounding');
  if (e.phase === 'grounding') {
    if (!heldLegs(e).length && !e.legs['front-left'].broken && !e.legs['front-right'].broken) {
      e.gaitTime += dt;
      e.lockPose = null;
    }
    refresh(e);
    lightning(e, dt);
    if (e.legs['front-left'].broken && e.legs['front-right'].broken) {
      e.lockPose = e.pose;
      enter(e, 'arrest');
    }
  }
  refresh(e);
  if (e.phase === 'arrest' && e.phaseAge >= SKY_CATHEDRAL_POLICY.arrestSeconds)
    enter(e, 'underpass');
  const radius = SKY_CATHEDRAL_SPACE.pilotRadius ?? 1.27;
  if (e.phase === 'underpass') {
    if (
      !e.enteredUnderpass &&
      crossed(e, 'underpassEntry', e.pose.portals.underpassEntry, e.previousPilot, e.pilot, radius)
    ) {
      e.enteredUnderpass = true;
      event(e, 'underpass-entered', null, e.pilot);
    }
    if (
      e.enteredUnderpass &&
      crossed(e, 'underpassExit', e.pose.portals.underpassExit, e.previousPilot, e.pilot, radius)
    ) {
      e.exitCrossed = true;
    }
    if (
      e.exitCrossed &&
      distance(e.pilot, e.pose.safeExit.center) + radius <= e.pose.safeExit.radius
    ) {
      // Recheck a blocked pose when the pilot moves or the short retry elapses,
      // rather than rebuilding the entire future sweep120 times each second.
      if (
        !e.kneelCheck ||
        distance(e.kneelCheck.point, e.pilot) > 0.1 ||
        e.age - e.kneelCheck.age > 0.5
      ) {
        e.kneelCheck = {
          point: copy(e.pilot),
          age: e.age,
          obstruction: skyCathedralKneelSweep(
            e.pilot,
            e.pilot,
            skyCathedralPoseInput(e),
            0,
            1,
            radius,
          ),
        };
      }
      if (!e.kneelCheck.obstruction) {
        e.exitedUnderpass = true;
        event(e, 'underpass-exited', null, e.pilot);
        enter(e, 'kneel');
      }
    }
  }
  if (e.phase === 'kneel' && e.phaseAge >= SKY_CATHEDRAL_POLICY.kneelSeconds) enter(e, 'nave');
  if (
    e.phase === 'nave' &&
    !e.enteredNave &&
    crossed(e, 'naveEntry', e.pose.portals.naveEntry, e.previousPilot, e.pilot, radius)
  ) {
    e.enteredNave = true;
    event(e, 'nave-entered', null, e.pilot);
  }
  if (e.phase === 'nave' && e.generator.broken) enter(e, 'discharge');
  if (e.phase === 'discharge' && e.phaseAge >= SKY_CATHEDRAL_POLICY.dischargeSeconds) {
    e.outcome = 'defeated';
    enter(e, 'complete');
  }
  if (e.phase === 'pilot-down' && e.phaseAge >= SKY_CATHEDRAL_POLICY.lossSeconds) {
    e.outcome = 'pilot-down';
    enter(e, 'lost');
  }
  refresh(e);
  return e;
}

/** Readiness and target exposure are canonical, never inferred from a cosmetic
 * pulse. Generator fire requires a real nave-entry crossing. */
export function skyCathedralSystems(e) {
  const aimedLeg = heldLegs(e)[0] ?? null;
  const aimPreparation = aimedLeg
    ? clamp((e.age - e.legs[aimedLeg].lockAt) / SKY_CATHEDRAL_POLICY.aimPrepareSeconds)
    : 1;
  return {
    controls: active(e) || (e.motionHeld && !['pilot-down', 'lost', 'complete'].includes(e.phase)),
    protected: !active(e) && !e.motionHeld,
    phase: e.phase,
    aimedLeg,
    aimPreparation,
    aimPreparing: Boolean(aimedLeg && aimPreparation < 1),
    openLegs: e.motionHeld ? [] : openLegs(e),
    generatorOpen: !e.motionHeld && e.phase === 'nave' && e.enteredNave && !e.generator.broken,
    disabledFences: ['left', 'right'].filter((side) => e.legs[`rear-${side}`].broken),
    kneel: skyCathedralPoseInput(e).kneel,
  };
}

/** Real81 intersection callback. Current target geometry is checked again;
 * malformed, repeated, closed or destroyed contacts remove no HP. The bounded
 * active-projectile ledger prevents repeated settlement without unbounded IDs. */
export function applySkyCathedralContact(e, { projectileId, componentId, point, damage } = {}) {
  const blocked = { handled: true, damage: 0, quality: 'armor' };
  if (
    !e ||
    !Number.isSafeInteger(projectileId) ||
    projectileId < 1 ||
    !finite(point) ||
    !Number.isFinite(damage) ||
    damage <= 0 ||
    damage > 100 ||
    e.hitLedger[projectileId] ||
    Object.keys(e.hitLedger).length >= 128
  )
    return blocked;
  const systems = skyCathedralSystems(e);
  const part = e.legs[componentId] ?? (componentId === 'generator' ? e.generator : null);
  const exposed =
    componentId === 'generator' ? systems.generatorOpen : systems.openLegs.includes(componentId);
  const target =
    componentId === 'generator'
      ? (e.pose.generator?.target ?? e.pose.generator)
      : e.pose.legs[componentId]?.target;
  if (!part || !exposed || part.broken || !target) return blocked;
  const centre = target.center ?? target;
  const radius = target.halfExtents
    ? Math.hypot(...Object.values(target.halfExtents))
    : target.radius;
  if (distance(point, centre) > radius + 0.001) return blocked;
  e.hitLedger[projectileId] = componentId;
  const removed = Math.min(part.hp, damage);
  part.hp -= removed;
  if (part.hp <= 1e-8) {
    part.hp = 0;
    part.broken = true;
    part.brokenAt = e.age;
    event(e, 'component-disabled', componentId, centre);
  }
  return { handled: true, damage: removed, quality: 'critical', disabled: part.broken };
}
